/**
 * Space Sharing Service
 * Handles sharing Mastermind Spaces via shareable links
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { SpaceShare, SpaceShareAccess, MastermindSpace } from "@shared/schema";
import type { ItemOverrideMap } from "@/services/sharedSpacePermissionService";
import type { SpaceMemberRole } from "@/services/sharedSpacePermissionService";
import type { WorkspaceRole } from "@/services/workspacesService";

export interface CreateShareLinkParams {
  spaceId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
  /** Role assigned to users who accept this invite (editor can edit shared content; viewer is read-only). */
  defaultMemberRole?: "editor" | "viewer";
}

export interface SpaceMemberEntry {
  userId: string;
  /** owner = mastermind owner; editor/viewer = shared members */
  spaceRole: SpaceMemberRole;
  status: "accepted";
  joinedAt: string;
  /** `space_share_access.id` — set for non-owner rows when known (required for singular revoke) */
  accessId?: string;
  /** When true, membership is mirrored from workspace share — space UI is read-only for this row */
  mirroredFromWorkspace?: boolean;
}

export interface SingularAccessBatchEntry {
  canLeave: boolean;
  mirroredFromWorkspace: boolean;
  accessId?: string;
}

export interface ShareLinkValidationResult {
  isValid: boolean;
  share: SpaceShare | null;
  space: MastermindSpace | null;
  access: SpaceShareAccess | null;
  message: string;
}

class SpaceSharingService {
  /**
   * Generate a unique share token
   */
  private generateShareToken(): string {
    // Generate a URL-safe token: base64url encoding of random bytes
    const randomBytes = new Uint8Array(24);
    crypto.getRandomValues(randomBytes);
    const base64 = btoa(String.fromCharCode(...randomBytes));
    // Convert to URL-safe base64url
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").substring(0, 32);
  }

  /**
   * Get current user ID from Supabase session
   */
  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  /**
   * Map database row (snake_case) to SpaceShare (camelCase)
   */
  private mapToSpaceShare(item: any): SpaceShare {
    return {
      id: item.id,
      spaceId: item.space_id,
      createdByUserId: item.created_by_user_id,
      shareToken: item.share_token,
      accessType: item.access_type,
      expiresAt: item.expires_at,
      isActive: item.is_active === "true",
      defaultMemberRole: (item.default_member_role as "editor" | "viewer") || "viewer",
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    } as SpaceShare & { defaultMemberRole?: "editor" | "viewer" };
  }

  private mapToSpaceShareAccess(item: any): SpaceShareAccess {
    return {
      id: item.id,
      shareId: item.share_id,
      userId: item.user_id,
      status: item.status,
      role: (item.role as "editor" | "viewer") || "viewer",
      accessedAt: item.accessed_at,
      respondedAt: item.responded_at,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    } as SpaceShareAccess & { role?: "editor" | "viewer" };
  }

  /** Map workspace membership to effective space role for workspace-scoped mastermind rows. */
  private workspaceRoleToSpaceRole(wsRole: WorkspaceRole): SpaceMemberRole {
    if (wsRole === "owner" || wsRole === "admin") return "editor";
    return "viewer";
  }

  /** Workspace member access to a mastermind row scoped to that workspace. */
  private async getWorkspaceMembershipForSpace(
    spaceId: string
  ): Promise<{ workspaceId: string; wsRole: WorkspaceRole } | null> {
    const { data: spaceRow } = await supabase
      .from("mastermind")
      .select("workspace_id")
      .eq("id", spaceId)
      .maybeSingle();

    const workspaceId = spaceRow?.workspace_id as string | null | undefined;
    if (!workspaceId) return null;

    const { workspacesService } = await import("@/services/workspacesService");
    const ws = await workspacesService.getWorkspace(workspaceId);
    if (!ws) return null;

    return { workspaceId, wsRole: ws.role };
  }

  /**
   * Create or get a share link for a space (owner must be the current user).
   * Works for personal spaces and workspace-scoped spaces — only ownership is required.
   */
  async createShareLink(params: CreateShareLinkParams): Promise<SpaceShare> {
    const userId = await this.getCurrentUserId();

    const { data: space, error: spaceError } = await supabase
      .from("mastermind")
      .select("id, name")
      .eq("id", params.spaceId)
      .eq("user_id", userId)
      .maybeSingle();

    if (spaceError || !space) {
      throw new Error("Space not found or you don't have permission to share it");
    }

    const { data: existingShare, error: existingErr } = await supabase
      .from("space_shares")
      .select("id, space_id, created_by_user_id, share_token, access_type, expires_at, is_active, default_member_role, created_at, updated_at")
      .eq("space_id", params.spaceId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .maybeSingle();

    if (existingErr) {
      throw new Error(`Failed to look up share link: ${existingErr.message}`);
    }

    if (existingShare) {
      // Update existing share if needed
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };
      if (params.accessType) updateData.access_type = params.accessType;
      if (params.expiresAt !== undefined) updateData.expires_at = params.expiresAt?.toISOString() || null;
      if (params.defaultMemberRole) updateData.default_member_role = params.defaultMemberRole;

      const { data: updatedShare, error: updateError } = await supabase
        .from("space_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to update share link: ${updateError.message}`);
      }

      return this.mapToSpaceShare(updatedShare);
    }

    // Create new share link
    const shareToken = this.generateShareToken();
    const shareData: any = {
      space_id: params.spaceId,
      created_by_user_id: userId,
      share_token: shareToken,
      access_type: params.accessType || "restricted",
      expires_at: params.expiresAt?.toISOString() || null,
      is_active: "true",
      default_member_role: params.defaultMemberRole || "viewer",
    };

    const { data: newShare, error: createError } = await supabase
      .from("space_shares")
      .insert(shareData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to create share link: ${createError.message}`);
    }

    return this.mapToSpaceShare(newShare);
  }

  /**
   * Validate a share link token and return share info
   */
  async validateShareLink(token: string): Promise<ShareLinkValidationResult> {
    // Get share by token
    const { data: share, error: shareError } = await supabase
      .from("space_shares")
      .select("id, space_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      return {
        isValid: false,
        share: null,
        space: null,
        access: null,
        message: "Link not valid or has been deactivated",
      };
    }

    // Check if expired
    if (share.expires_at) {
      const expiresAt = new Date(share.expires_at);
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          share: this.mapToSpaceShare(share),
          space: null,
          access: null,
          message: "This share link has expired",
        };
      }
    }

    // Get space info
    const { data: space, error: spaceError } = await supabase
      .from("mastermind")
      .select("id, user_id, name, description, icon, color, goals, agents, linked_source_ids, milestones, created_at, updated_at")
      .eq("id", share.space_id)
      .maybeSingle();

    if (spaceError || !space) {
      return {
        isValid: false,
        share: this.mapToSpaceShare(share),
        space: null,
        access: null,
        message: "Space not found or has been deleted",
      };
    }

    // Check if user is authenticated
    let access: SpaceShareAccess | null = null;
    try {
      const userId = await this.getCurrentUserId();

      // Check if user already has access record
      const { data: accessRecord } = await supabase
        .from("space_share_access")
        .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
        .eq("share_id", share.id)
        .eq("user_id", userId)
        .single();

      if (accessRecord) {
        access = this.mapToSpaceShareAccess(accessRecord);
      }
    } catch {
      // User not authenticated - that's okay, they'll need to sign in
    }

    return {
      isValid: true,
      share: this.mapToSpaceShare(share),
      space: {
        id: space.id,
        name: space.name,
        description: space.description,
        icon: space.icon,
        color: space.color,
        goals: space.goals || [],
        agents: space.agents || [],
        linkedSourceIds: space.linked_source_ids || [],
        milestones: space.milestones || [],
        lens: space.lens || { purpose: "", priorities: "", details: "" },
        isLocked: space.is_locked === "true",
        lockedAt: space.locked_at,
        createdAt: space.created_at,
        updatedAt: space.updated_at,
      } as MastermindSpace,
      access,
      message: "Link is valid",
    };
  }

  /**
   * Accept a share invitation
   */
  async acceptShareInvitation(token: string): Promise<SpaceShareAccess> {
    const userId = await this.getCurrentUserId();

    // Get share by token
    const { data: share, error: shareError } = await supabase
      .from("space_shares")
      .select("id, space_id, created_by_user_id, share_token, access_type, expires_at, is_active, default_member_role, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      throw new Error("Share link not found or invalid");
    }

    const inviteRole = ((share as any).default_member_role as "editor" | "viewer") || "viewer";

    // Check if access already exists
    const { data: existingAccess } = await supabase
      .from("space_share_access")
      .select("id, share_id, user_id, status, role, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      // Update status if needed
      if (existingAccess.status !== "accepted") {
        const { data: updatedAccess, error: updateError } = await supabase
          .from("space_share_access")
          .update({
            status: "accepted",
            role: inviteRole,
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingAccess.id)
          .select()
          .single();

        if (updateError) {
          throw new Error(`Failed to accept invitation: ${updateError.message}`);
        }

        return this.mapToSpaceShareAccess(updatedAccess);
      }

      return this.mapToSpaceShareAccess(existingAccess);
    }

    // Create new access record
    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "accepted",
      role: inviteRole,
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("space_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to accept invitation: ${createError.message}`);
    }

    return this.mapToSpaceShareAccess(newAccess);
  }

  /**
   * Reject a share invitation
   */
  async rejectShareInvitation(token: string): Promise<SpaceShareAccess> {
    const userId = await this.getCurrentUserId();

    // Get share by token
    const { data: share, error: shareError } = await supabase
      .from("space_shares")
      .select("id, space_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      throw new Error("Share link not found or invalid");
    }

    // Check if access already exists
    const { data: existingAccess } = await supabase
      .from("space_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      // Update status
      const { data: updatedAccess, error: updateError } = await supabase
        .from("space_share_access")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingAccess.id)
        .select()
        .single();

      if (updateError) {
        throw new Error(`Failed to reject invitation: ${updateError.message}`);
      }

      return this.mapToSpaceShareAccess(updatedAccess);
    }

    // Create new access record with rejected status
    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "rejected",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("space_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) {
      throw new Error(`Failed to reject invitation: ${createError.message}`);
    }

    return this.mapToSpaceShareAccess(newAccess);
  }

  /**
   * Revoke a share link (deactivate it)
   */
  async revokeShareLink(spaceId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("space_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("space_id", spaceId)
      .eq("created_by_user_id", userId);

    if (error) {
      throw new Error(`Failed to revoke share link: ${error.message}`);
    }

    return true;
  }

  /**
   * Get share link for a space (if exists)
   */
  async getShareLink(spaceId: string): Promise<SpaceShare | null> {
    const userId = await this.getCurrentUserId();

    const { data: share, error } = await supabase
      .from("space_shares")
      .select("id, space_id, created_by_user_id, share_token, access_type, expires_at, is_active, default_member_role, created_at, updated_at")
      .eq("space_id", spaceId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .maybeSingle();

    if (error || !share) {
      return null;
    }

    return this.mapToSpaceShare(share);
  }

  /**
   * Check if current user has access to a space via share
   */
  async hasSpaceAccess(spaceId: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();

      // Check if user owns the space (use maybeSingle: non-owners get 0 rows — .single() causes PGRST116)
      const { data: ownedSpace } = await supabase
        .from("mastermind")
        .select("id")
        .eq("id", spaceId)
        .eq("user_id", userId)
        .maybeSingle();

      if (ownedSpace) {
        return true;
      }

      // Get active shares for this space
      const { data: shares } = await supabase
        .from("space_shares")
        .select("id")
        .eq("space_id", spaceId)
        .eq("is_active", "true");

      if (!shares || shares.length === 0) {
        const wsMembership = await this.getWorkspaceMembershipForSpace(spaceId);
        if (wsMembership) {
          return true;
        }
        const { data: spaceRow } = await supabase
          .from("mastermind")
          .select("user_id, workspace_id, name")
          .eq("id", spaceId)
          .maybeSingle();
        return false;
      }

      const shareIds = shares.map(s => s.id);

      // Check if user has accepted share access
      const { data: access } = await supabase
        .from("space_share_access")
        .select("id, status")
        .eq("user_id", userId)
        .eq("status", "accepted")
        .in("share_id", shareIds)
        .maybeSingle();

      const granted = !!access;
      if (!granted) {
        const wsMembership = await this.getWorkspaceMembershipForSpace(spaceId);
        if (wsMembership) {
          return true;
        }
      }
      return granted;
    } catch (error) {
      console.error("hasSpaceAccess check failed, treating as no access:", error);
      return false;
    }
  }

  /**
   * Check if current user is the owner of a space
   */
  async isSpaceOwner(spaceId: string): Promise<boolean> {
    try {
      const userId = await this.getCurrentUserId();
      const { data: space } = await supabase
        .from("mastermind")
        .select("user_id")
        .eq("id", spaceId)
        .eq("user_id", userId)
        .maybeSingle();

      return !!space;
    } catch {
      return false;
    }
  }

  /**
   * Get all members of a shared space (owner + accepted members).
   * Workspace-mirrored members include `mirroredFromWorkspace: true` when the caller is the space owner
   * and the DB RPC is available (required for workspace-scoped spaces).
   */
  async getSpaceMembers(spaceId: string): Promise<SpaceMemberEntry[]> {
    try {
      let currentUserId = "";
      try {
        currentUserId = await this.getCurrentUserId();
      } catch {
        currentUserId = "";
      }

      const { data: space } = await supabase
        .from("mastermind")
        .select("user_id, created_at, workspace_id")
        .eq("id", spaceId)
        .maybeSingle();

      if (!space) {
        return [];
      }

      const ownerUserId = String(space.user_id);
      const workspaceId = space.workspace_id as string | null | undefined;
      const isOwner = currentUserId !== "" && ownerUserId === currentUserId;

      const members: SpaceMemberEntry[] = [
        {
          userId: ownerUserId,
          spaceRole: "owner",
          status: "accepted",
          joinedAt: space.created_at,
        },
      ];

      const { data: shares } = await supabase
        .from("space_shares")
        .select("id")
        .eq("space_id", spaceId)
        .eq("is_active", "true");

      if (!shares?.length) {
        return members;
      }

      const shareIds = shares.map(s => s.id);
      const { data: accessRecords } = await supabase
        .from("space_share_access")
        .select("id, user_id, responded_at, role")
        .in("share_id", shareIds)
        .eq("status", "accepted");

      const byUser = new Map<
        string,
        { id: string; user_id: string; responded_at: string | null; role: string | null }
      >();
      for (const access of accessRecords || []) {
        const uid = String(access.user_id);
        if (uid === ownerUserId) continue;
        const prev = byUser.get(uid);
        if (!prev) {
          byUser.set(uid, {
            id: String(access.id),
            user_id: uid,
            responded_at: access.responded_at,
            role: access.role,
          });
          continue;
        }
        const prevRank = prev.role === "editor" ? 1 : 0;
        const curRank = access.role === "editor" ? 1 : 0;
        if (curRank > prevRank) {
          byUser.set(uid, {
            id: String(access.id),
            user_id: uid,
            responded_at: access.responded_at,
            role: access.role,
          });
        }
      }

      const pushMember = (
        uid: string,
        role: "editor" | "viewer",
        joinedAt: string,
        accessId: string | undefined,
        mirrored: boolean | undefined
      ) => {
        members.push({
          userId: uid,
          spaceRole: role,
          status: "accepted",
          joinedAt,
          accessId,
          mirroredFromWorkspace: mirrored,
        });
      };

      if (!workspaceId) {
        for (const row of byUser.values()) {
          const sr = (row.role as "editor" | "viewer") || "viewer";
          pushMember(
            row.user_id,
            sr,
            row.responded_at || new Date().toISOString(),
            row.id,
            false
          );
        }
        return members;
      }

      if (isOwner) {
        const { data: flagsData, error: flagsErr } = await supabase.rpc("space_share_access_member_flags", {
          p_space_id: spaceId,
        });
        if (flagsErr) {
          console.error("space_share_access_member_flags:", flagsErr);
          for (const row of byUser.values()) {
            const sr = (row.role as "editor" | "viewer") || "viewer";
            pushMember(
              row.user_id,
              sr,
              row.responded_at || new Date().toISOString(),
              row.id,
              true
            );
          }
          return members;
        }
        const flagRows = Array.isArray(flagsData)
          ? flagsData
          : typeof flagsData === "string"
            ? (JSON.parse(flagsData) as unknown[])
            : ((flagsData as unknown[]) ?? []);
        for (const fr of flagRows as Array<{
          user_id: string;
          access_id: string;
          role: string;
          responded_at: string | null;
          mirrored_from_workspace: boolean;
        }>) {
          const sr = (fr.role as "editor" | "viewer") || "viewer";
          pushMember(
            String(fr.user_id),
            sr,
            fr.responded_at || new Date().toISOString(),
            String(fr.access_id),
            fr.mirrored_from_workspace === true
          );
        }
        return members;
      }

      for (const row of byUser.values()) {
        const sr = (row.role as "editor" | "viewer") || "viewer";
        pushMember(row.user_id, sr, row.responded_at || new Date().toISOString(), row.id, undefined);
      }
      return members;
    } catch (error) {
      console.error("Error fetching space members:", error);
      return [];
    }
  }

  /**
   * Leave a singular (non–workspace-mirrored) space share. Uses SECURITY DEFINER RPC.
   */
  async leaveSingularSpace(spaceId: string): Promise<void> {
    const { error } = await supabase.rpc("shared_leave_singular_space", { p_space_id: spaceId });
    if (error) {
      throw new Error(error.message || "Could not leave space");
    }
  }

  /**
   * Batch: whether the current user can leave each space (singular link only) — for list overflow menus.
   */
  async getCurrentUserSingularAccessBatch(spaceIds: string[]): Promise<Record<string, SingularAccessBatchEntry>> {
    if (spaceIds.length === 0) {
      return {};
    }
    try {
      await this.getCurrentUserId();
    } catch {
      return Object.fromEntries(spaceIds.map(id => [id, { canLeave: false, mirroredFromWorkspace: false }]));
    }
    const { data, error } = await supabase.rpc("current_user_singular_space_access_batch", {
      p_space_ids: spaceIds,
    });
    if (error) {
      console.error("current_user_singular_space_access_batch:", error);
      return Object.fromEntries(spaceIds.map(id => [id, { canLeave: false, mirroredFromWorkspace: false }]));
    }
    const raw = (data ?? {}) as Record<string, { can_leave?: boolean; mirrored_from_workspace?: boolean; access_id?: string }>;
    const out: Record<string, SingularAccessBatchEntry> = {};
    for (const sid of spaceIds) {
      const row = raw[sid];
      if (!row) {
        out[sid] = { canLeave: false, mirroredFromWorkspace: false };
        continue;
      }
      out[sid] = {
        canLeave: row.can_leave === true,
        mirroredFromWorkspace: row.mirrored_from_workspace === true,
        accessId: typeof row.access_id === "string" ? row.access_id : undefined,
      };
    }
    return out;
  }

  /**
   * Mastermind space owner user id (for permission checks).
   */
  async getSpaceOwnerUserId(spaceId: string): Promise<string | null> {
    const { data: space } = await supabase
      .from("mastermind")
      .select("user_id")
      .eq("id", spaceId)
      .maybeSingle();
    return space?.user_id ? String(space.user_id) : null;
  }

  /**
   * Current user's role in the space: owner (mastermind owner), editor, viewer, or null.
   */
  async getCurrentUserSpaceRole(spaceId: string): Promise<SpaceMemberRole | null> {
    const userId = await this.getCurrentUserId();
    const { data: owned } = await supabase
      .from("mastermind")
      .select("id")
      .eq("id", spaceId)
      .eq("user_id", userId)
      .maybeSingle();
    if (owned) return "owner";

    const { data: shares } = await supabase
      .from("space_shares")
      .select("id")
      .eq("space_id", spaceId)
      .eq("is_active", "true");
    if (!shares?.length) return null;

    const shareIds = shares.map(s => s.id);
    const { data: access } = await supabase
      .from("space_share_access")
      .select("role")
      .eq("user_id", userId)
      .eq("status", "accepted")
      .in("share_id", shareIds)
      .maybeSingle();

    if (!access) {
      const wsMembership = await this.getWorkspaceMembershipForSpace(spaceId);
      if (wsMembership) {
        return this.workspaceRoleToSpaceRole(wsMembership.wsRole);
      }
      return null;
    }
    return ((access as any).role as "editor" | "viewer") || "viewer";
  }

  /**
   * Item-level overrides for the current user in this space (from space_item_permissions).
   */
  async getMyItemOverridesForSpace(spaceId: string): Promise<ItemOverrideMap> {
    const userId = await this.getCurrentUserId();
    const { data: rows, error } = await supabase
      .from("space_item_permissions")
      .select("item_type, item_id, access_level")
      .eq("space_id", spaceId)
      .eq("subject_user_id", userId);

    if (error || !rows?.length) return {};

    const map: ItemOverrideMap = {};
    for (const r of rows as any[]) {
      const k = `${r.item_type}:${r.item_id}`;
      map[k] = r.access_level === "editor" ? "editor" : "viewer";
    }
    return map;
  }

  /** Space owner: change a member's editor/viewer role (singular members only; RPC rejects workspace mirrors) */
  async updateMemberSpaceRole(spaceId: string, memberUserId: string, role: "editor" | "viewer"): Promise<void> {
    const { error } = await supabase.rpc("shared_update_singular_space_member_role", {
      p_space_id: spaceId,
      p_member_user_id: memberUserId,
      p_role: role,
    });
    if (error) throw new Error(error.message || "Failed to update member role");
  }

  /** Space owner: set per-item access for a user */
  async setItemPermission(
    spaceId: string,
    itemType: "topic" | "thread" | "document",
    itemId: string,
    subjectUserId: string,
    accessLevel: "editor" | "viewer"
  ): Promise<void> {
    const { error } = await supabase.rpc("shared_set_item_permission", {
      p_space_id: spaceId,
      p_item_type: itemType,
      p_item_id: itemId,
      p_subject_user_id: subjectUserId,
      p_access_level: accessLevel,
    });
    if (error) throw new Error(error.message || "Failed to set item permission");
  }

  async removeItemPermission(
    spaceId: string,
    itemType: "topic" | "thread" | "document",
    itemId: string,
    subjectUserId: string
  ): Promise<void> {
    const { error } = await supabase.rpc("shared_remove_item_permission", {
      p_space_id: spaceId,
      p_item_type: itemType,
      p_item_id: itemId,
      p_subject_user_id: subjectUserId,
    });
    if (error) throw new Error(error.message || "Failed to remove item permission");
  }

  /**
   * Remove a singular member by `space_share_access` row id (workspace mirrors rejected in RPC).
   */
  async removeMember(spaceId: string, accessId: string): Promise<boolean> {
    const isOwner = await this.isSpaceOwner(spaceId);
    if (!isOwner) {
      throw new Error("Only the space owner can remove members");
    }

    const { error } = await supabase.rpc("shared_revoke_singular_space_member", {
      p_space_id: spaceId,
      p_access_id: accessId,
    });

    if (error) {
      throw new Error(error.message || "Failed to remove member");
    }

    return true;
  }
}

export const spaceSharingService = new SpaceSharingService();

